<template>
  <ConfirmDialog/>
  <Toast group="mainToast" position="top-center"/>
  <Toolbar></Toolbar>
  <ProgressSpinnerContainer v-if="loading" class="mt-3"/>
  <div v-else class="main">
    <NavBar></NavBar>
    <MainContent></MainContent>
  </div>
  <Dialog :visible="selectAccountDialogVisible"
          header="Select a Sphere"
          modal
          @update:visible="handleAccountSelectDialogClose">
    <ScrollPanel class="scroll-panel">
      <Listbox v-model="selectedAccount" :options="nestedAccounts" optionLabel="name" @change="accountSelectEvent"/>
    </ScrollPanel>
  </Dialog>
  <Dialog :visible="createAccountDialogVisible"
          header="Create a Sphere"
          modal
          @update:visible="handleAccountCreateDialogClose">
    <div class="form-element">
      Are you a Company or an Individual?
      <SelectButton v-model="accountCreateType" :options="accountCreateTypeOptions" :allow-empty="false"></SelectButton>
    </div>
    <div class="form-element">
      Name:
      <InputText v-model="accountCreateName" :invalid="accountCreateNameInvalid" class="form-element" type="text"
                 @change="accountCreateNameChange"/>
      <div v-if="accountCreateNameInvalid">
        <InlineMessage severity="error">Name is required.</InlineMessage>
      </div>
    </div>
    <div class="form-element" v-if="selectedAccount">
      Is this a new Sphere, or should it be created under another sphere?
      <SelectButton v-model="accountCreateSubaccount"
                    :options="accountCreateSubaccountOptions"
                    :allow-empty="false"
                    @change="accountCreateSelectedSubaccountChange"></SelectButton>
      <div v-if="accountCreateSubaccount === 'Sub-sphere'">
        Parent Sphere:
        <Dropdown
                  :invalid="accountCreateSelectedSubaccountInvalid"
                 :options="nestedAccounts"
                  option-label="name" option-value="id"
                  v-model="accountCreateSelectedSubaccount"
                  class="mt"
                  filter
                  @change="accountCreateSelectedSubaccountChange"
                  :auto-filter-focus="true"
        >
          <template #value="slotProps">
            <div v-if="slotProps.value" class="flex align-items-center">
              {{ getSphereById(slotProps.value) }}
            </div>
            <span v-else>
                Select a Sphere...
              </span>
          </template>
        </Dropdown>
        <div v-if="accountCreateSelectedSubaccountInvalid">
          <InlineMessage severity="error">You must select a parent Sphere.</InlineMessage>
        </div>
      </div>

    </div>
    <template #footer>
      <div class="align-right">
        <Button :disabled="accountCreateSubmitting" :loading="accountCreateSubmitting" label="Create" type="button"
                @click="submitAccount"></Button>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { useAuth0 } from "@auth0/auth0-vue";
import ConfirmDialog from "primevue/confirmdialog";
import Toast from "primevue/toast";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";

import { computed, nextTick, ref, unref, watch } from "vue";
import NavBar from "../components/NavBar.vue";
import ProgressSpinnerContainer from "../components/ProgressSpinnerContainer.vue";
import Toolbar from "../components/Toolbar.vue";
import {
  loadKnowledgeSources, loadLlmQuotas,
  loadModelDeployments, loadSkills,
  loadUserGroups,
  useAccounts,
  useUsers
} from "../composables/entities.js";

import { selectedAccount } from "../store/state.js";
import { post } from "../util/api.js";
import { decodeAccount } from "../util/util.js";
import MainContent from "./MainContent.vue";

const { isAuthenticated, checkSession, user } = useAuth0()

const toast = useToast()

await checkSession()

const {
  accounts,
  accountsLoading,
  loadAccounts,
  createAccountDialogVisible,
  selectAccountDialogVisible,
  showAccountDialog,
  nestedAccounts
} = useAccounts()


const { loadUsers, usersLoading } = useUsers();

const loading = computed(() => {
  return accountsLoading.value || usersLoading.value || selectAccountDialogVisible.value || (createAccountDialogVisible.value && accounts.value?.length === 0)
})

const accountCreateType = ref('Company')
const accountCreateTypeOptions = ref(['Company', 'Individual'])
const accountCreateName = ref('')
const accountCreateNameInvalid = ref(false)
const accountCreateSubmitting = ref(false)
const accountCreateSubaccount = ref(accounts.value.length > 0 ? 'Sub-sphere' : 'New')
const accountCreateSubaccountOptions = ref(['New', 'Sub-sphere'])
const accountCreateSelectedSubaccount = ref(null)
const accountCreateSelectedSubaccountInvalid = ref(false)

function accountCreateNameChange() {
  accountCreateNameInvalid.value = false
}
function accountCreateSelectedSubaccountChange() {
  accountCreateSelectedSubaccountInvalid.value = false
}

function getSphereById(accountId) {
  for (let i = 0; i < accounts.value.length; i++) {
    if (String(accounts.value[i].id) === String(accountId)) {
      return accounts.value[i].name
    }
  }
  return ''
}

const confirm = useConfirm()

async function submitAccount() {
  const n = unref(accountCreateName)
  let invalid = false
  if (n.length < 1) {
    accountCreateNameInvalid.value = true
    invalid = true
  }
  if (
      (accountCreateSubaccount.value === 'Sub-sphere') &&
      (accountCreateSelectedSubaccount.value === null)
  ) {
    accountCreateSelectedSubaccountInvalid.value = true
    invalid = true
  }
  if (invalid) {
    return
  }

  accountCreateSubmitting.value = true

  try {
    const data = {
      "account_name": n,
      "metadata": {
        "sustain": {
          "type": accountCreateType.value
        }
      }
    }
    if (accountCreateSubaccount.value === 'Sub-sphere') {
      data['cephisso_platform_accounts_id'] = accountCreateSelectedSubaccount.value
      for (let i = 0; i < accounts.value.length; i++) {
        if (String(accounts.value[i].id) === String(accountCreateSelectedSubaccount.value)) {
          break;
        }
      }
    }



    let showDuplicateWarning = false
    for (let i = 0; i < accounts.value.length; i++) {
      // If there's a parent ID, check for an account with the same name and same parent ID
      if ('cephisso_platform_accounts_id' in data) {
        if (
            (String(accounts.value[i].parentId) === String(data['cephisso_platform_accounts_id'])) &&
            (accounts.value[i].name?.toLowerCase() === n?.toLowerCase())
          ) {
          showDuplicateWarning = true
          break
        }
      }
      else {
        // If no parent ID, check for a root account with same name
        if (
            (!accounts.value[i].parentId) &&
            (accounts.value[i].name?.toLowerCase() === n?.toLowerCase())
        ) {
          showDuplicateWarning = true
          break
        }
      }
    }

    async function continueAccountCreate() {
      data.metadata = JSON.stringify(data.metadata)

      const p = await post('/v1/cephisso-platform-accounts', data)

      handleAccountCreateDialogClose()
      const newAccount = decodeAccount(p)
      accounts.value.push(newAccount)
      selectedAccount.value = newAccount.id
      await loadData()
    }

    if (showDuplicateWarning) {
      confirm.require({
        message: 'A Sphere with the name ' + n + ' already exists at this level. Are you sure you wish to create?',
        header: 'Duplicate Sphere',
        icon: 'pi pi-info-circle',
        rejectLabel: 'Cancel',
        acceptLabel: 'Create',
        rejectClass: 'p-button-secondary p-button-outlined',
        acceptClass: 'p-button-danger',
        accept: async () => {
          accountCreateSubmitting.value = true
          await continueAccountCreate()
          accountCreateSubmitting.value = false
          return true
        },
        reject: () => {
          return true
        }
      })
    }
    else {
      await continueAccountCreate()
    }

  } catch (err) {
    toast.add({
      severity: 'error',
      group: 'mainToast',
      life: 5000,
      detail: 'An error has occurred.  Please try again.'
    })
  }

  accountCreateSubmitting.value = false
}

function handleAccountSelectDialogClose() {
  selectAccountDialogVisible.value = false
}
function handleAccountCreateDialogClose() {
  accountCreateName.value = ''
  accountCreateNameInvalid.value = false
  accountCreateType.value = 'Company'
  accountCreateSubaccount.value = 'New'
  accountCreateSelectedSubaccount.value = null
  accountCreateSelectedSubaccountInvalid.value = false
  accountCreateSubmitting.value = false
  createAccountDialogVisible.value = false
}

function accountSelectEvent(event) {
  selectedAccount.value = event.value.id
  selectAccountDialogVisible.value = false
}

async function loadData() {
  await Promise.all([
    loadUsers(),
    loadUserGroups(),
    loadKnowledgeSources(),
    loadModelDeployments(),
    loadLlmQuotas(),
    loadSkills()
  ])
}

watch(selectedAccount,
    (newValue) => {
      if (newValue) {
        loadData()
      }
    },
    { immediate: true }
)

watch(isAuthenticated,
    async (newValue) => {
      if (newValue) {
        await loadAccounts()

        if (accounts.value.length === 1) {
          selectedAccount.value = accounts.value[0].id
        }
        else {
          if (!selectedAccount.value) {
            nextTick(() => showAccountDialog())
          }
        }
      }
    },
    { immediate: true }
)

</script>

<style scoped>
.main {
  color: #ffffff;
}
.main-footer {
  text-align: center;
  color: #ffffff;
  z-index: 1000;
  margin-bottom: .5em;
}

@media (min-width: 1024px) {
  .main {
    display: flex;
    justify-content: space-around;
  }
  .main-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
  }
  .scroll-panel {
    height: 70vh;
  }
  .scroll-panel.smaller {
    height: 60vh;
  }
}

</style>
